// site-details.jsx
import { useState, useEffect, useRef } from 'react';
import { Layout, Row, Col } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { Modal, Card, Divider, Input } from 'antd';
import { Typography, Descriptions, Select } from 'antd';
import { Button, Form, Tag } from 'antd';
import clsx from 'clsx';
import { isEqual, isEmpty } from 'lodash';
import SignatureCanvas from 'react-signature-canvas';
import BasicLayout from '@/components/common/layout/basic';
import MaterialSearchTable from '@components/sites/MaterialSearchTable';
import LanguageSwitcher from '@common/LanguageSwitcher';
import i18n from '@/i18n';
import { t } from '@/utils/translator';
import useAuthStore from '@store/useAuthStore';
import { getPreferredTimezone, formatDateRange } from '@/utils/dateFormatter';
import { ProjectStatusCode, ProjectStatusCodeColor, EstimateCylcle } from '@constants/status';
import { STYLE_CONSTANTS, MOCK_SITE_USERS } from '@/constants/siteData';

import api from '@/api/api';
import {
  useSiteDetails,
  useModalStates,
  useUserManagement,
  useSiteManagement,
} from '@/hooks/useSiteDetails';
import SiteDescriptionCard from '@/components/user/site-details/SiteDescriptionCard';
import EstimateCycleTable from '@/components/user/site-details/EstimateCycleTable';
import ProjectAnnouncementModal from '@/components/user/site-details/modals/ProjectAnnouncementModal';
import SiteEditModal from '@/components/user/site-details/modals/SiteEditModal';

const { Content, Sider } = Layout;
const { Title, Paragraph } = Typography;

import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';

dayjs.extend(duration);

function UserSiteDetailed() {
  const {
    project_code,
    company_id,
    user,
    isLoadedState,
    EstimateProject,
    EstimateSites,
    setEstimateSites,
    ActiveEstimateSite,
    setActiveEstimateSite,
    EstimateCycle,
    ProjectDocuments,
    RequiredDocuments,
    OptionalDocuments,
    refreshSitesList,
    refreshCycleData,
  } = useSiteDetails();

  const {
    isShowProjectAnnouncementModal,
    showProjectAnnouncementModal,
    isShowProjectAgreeModal,
    setShowProjectAgreeModal,
    isShowUsersModal,
    setShowUsersModal,
    isShowSiteEditModal,
    setSiteEditModal,
    isShowScope3TemplateModal,
    setShowScope3TemplateModal,
    isSiteEditModal,
    setSiteModalEdittype,
  } = useModalStates();

  const {
    siteUsers,
    setSiteUsers,
    editUser,
    setEditUser,
    isDirty,
    setIsDirty,
    isPasswordChange,
    setPasswordChange,
    addNewUser,
    openUserModal,
    resetUserModal,
  } = useUserManagement();

  const { saveSiteEditedNewSite } = useSiteManagement();

  // 템플릿 타입 선택 상태
  const [templateType, setTemplateType] = useState('latest'); // 'legacy' or 'latest'

  // 사용자 시간대 가져오기
  const userTimezone = getPreferredTimezone();

  // policy_agree 체크 및 모달 자동 열기
  useEffect(() => {
    if (isLoadedState && EstimateProject?.estimates?.[0]?.policy_agree === false) {
      setShowProjectAgreeModal(true);
    } else {
      showProjectAnnouncementModal(true);
    }
  }, [isLoadedState, EstimateProject, setShowProjectAgreeModal, showProjectAnnouncementModal]);

  useEffect(() => {
    if (EstimateProject?.estimates?.[0]?.policy_agree === true && !isShowProjectAgreeModal) {
      showProjectAnnouncementModal(true);
    }
  }, [EstimateProject, isShowProjectAgreeModal, showProjectAnnouncementModal]);

  /*** dummy 데이터 ***/
  const SiteInfoSet = {
    1: {
      site_name: t('협력사 사업장', { number: 1 }),
      site_company_name: `${t('협력사')} ${t('기업명')}`,
      site_estimate_progress: '진행중',
      site_country: 'Korea',
      site_address: `${t('협력사')} ${t('사업장 주소')}`,
      site_clients:
        i18n.language === 'ko'
          ? 'HL만도 국내'
          : i18n.language === 'zh'
            ? 'HL만도 중국'
            : 'HL만도 미국',
    },
  };
  EstimateCylcle[1] = EstimateCylcle[1]?.sort((a, b) => b.cycle_index - a.cycle_index);
  /*** dummy 데이터 ***/

  // ***** 참여자 관리 *****
  const [userEditForm] = Form.useForm();

  const handleReset = () => {
    userEditForm.setFieldsValue(editUser);
    setIsDirty(false);
  };

  const openUserModalHandler = () => {
    setSiteUsers(MOCK_SITE_USERS);
    setShowUsersModal(true);
  };

  const closeUserModal = () => {
    setShowUsersModal(false);
    resetUserModal();
  };

  const addNewUserHandler = () => {
    const newUser = addNewUser();
    userEditForm.setFieldsValue(newUser);
  };
  // ***** 참여자 관리 *****

  /*** 사업장 추가 및 수정 관련 ***/
  const [siteEditForm] = Form.useForm();

  const openNewSiteModal = () => {
    setSiteModalEdittype(false);
    siteEditForm.setFieldsValue({
      site_name: null,
      site_country: null,
      site_location: null,
      site_clients: [],
    });
    setSiteEditModal(true);
  };

  const openSiteInfoEditModal = (_Site) => {
    setSiteModalEdittype(true);
    siteEditForm.setFieldsValue(_Site);
    setSiteEditModal(true);
  };

  const closeSiteInfoEditModal = () => {
    setSiteModalEdittype(true);
    setSiteEditModal(false);
    siteEditForm.setFieldsValue({});
  };

  const initSiteInfoEditModal = () => {
    siteEditForm.setFieldsValue({
      site_name: null,
      site_country: null,
      site_address: null,
      site_clients: [],
    });
  };

  const saveSiteHandler = async () => {
    try {
      // 폼 검증 실행
      await siteEditForm.validateFields();

      const editedSite = siteEditForm.getFieldsValue();
      const EditingSite = EstimateSites.find(({ key }) => ActiveEstimateSite === key);

      const savedSite = await saveSiteEditedNewSite(
        editedSite,
        isSiteEditModal,
        EditingSite?.id,
        EstimateProject?.id
      );

      // 사업장 저장 후 리스트 갱신
      if (savedSite) {
        const updatedSites = await refreshSitesList();

        // 새로 추가된 사업장이면 해당 사업장을 활성화
        if (!isSiteEditModal && savedSite.id) {
          setActiveEstimateSite(savedSite.id);
        }
      }

      setSiteEditModal(false);
    } catch (error) {
      console.error('Error saving site:', error);
    }
  };
  /*** 사업장 추가 및 수정 관련 ***/

  /*** Scope 3 Cat1 산정 템플릿 다운로드 ***/
  function setScope3TemplateModal(isModalOpened) {
    setShowScope3TemplateModal(isModalOpened);
  }

  function downloadEmissionTemplate() {
    const basePath = '/documents/hlmando25/';
    let fileName = '';

    if (templateType === 'legacy') {
      // Excel 2016 등 구버전 호환용
      switch (i18n.language) {
        case 'ko':
          fileName = 'QESG_HL만도 공급망 온실가스 산정 템플릿_2016v2.1.xlsm';
          break;
        case 'en':
          fileName =
            "QESG_Template for GHG Emission Calculation of HL Mando's Supply Chain_(company name)_2016v2.1.xlsm";
          break;
        case 'zh':
          fileName = 'QESG_HL Mando供应链温室气体排放核算模板_(企业名称)_2016v2.1.xlsm';
          break;
        default:
          fileName = 'QESG_HL만도 공급망 온실가스 산정 템플릿_2016v2.1.xlsm';
          break;
      }
    } else {
      // 온실가스 산정 템플릿 (최신버전)
      switch (i18n.language) {
        case 'ko':
          fileName = 'QESG_HL만도 공급망 온실가스 산정 템플릿_(기업명)ver3.1.xlsm';
          break;
        case 'en':
          fileName =
            "QESG_Template for GHG Emission Calculation of HL Mando's Supply Chain_(company name)ver3.1.xlsm";
          break;
        case 'zh':
          fileName = 'QESG_HL Mando供应链温室气体排放核算模板_(企业名称)ver3.1.xlsm';
          break;
        default:
          fileName = 'QESG_HL만도 공급망 온실가스 산정 템플릿_(기업명)ver3.1.xlsm';
          break;
      }
    }

    const link = document.createElement('a');
    link.href = basePath + fileName;
    link.download = fileName;
    link.click();
  }

  function downloadManual() {
    const basePath = '/documents/hlmando25/';
    let fileName = '';

    switch (i18n.language) {
      case 'ko':
        fileName = '(QESG) 2025 HL 만도 협력사 온실가스 배출량 산정 교육.pdf';
        break;
      case 'en':
        fileName = '(QESG) 2025 GHG Emissions Calculation for HL Mando’s Global Suppliers.pdf';
        break;
      case 'zh':
        fileName = '(QESG) 2025 HL Mando 合作伙伴温室气体排放量核算培训.pdf';
        break;
      default:
        fileName = '(QESG) 2025 HL 만도 협력사 온실가스 배출량 산정 교육.pdf';
        break;
    }

    const link = document.createElement('a');
    link.href = basePath + fileName;
    link.download = fileName;
    link.click();
  }
  /*** Scope 3 Cat1 산정 템플릿 다운로드 ***/

  function EstimateAgree() {
    const user = useAuthStore((state) => state.user);
    const [isPolicyAgree, setPolicyAgree] = useState(false);
    const [agreeDate, setAgreeDate] = useState(null);
    const [isAbleToAgreeSubmit, setAbleToAgreeSubmit] = useState(false);
    const sigCanvas = useRef(null);

    const onChangeAgree = (e) => {
      setPolicyAgree(e.target.value);
    };

    useEffect(() => {
      setShowProjectAgreeModal(true);
      setAgreeDate(new Date().toLocaleDateString());
    }, []);

    function handlePolicySubmitAvailable() {
      if (!sigCanvas.current.isEmpty()) {
        setAbleToAgreeSubmit(true);
      } else {
        setAbleToAgreeSubmit(false);
      }
    }

    function clearPolicySignature() {
      sigCanvas.current.clear();
      setAbleToAgreeSubmit(false);
    }

    async function handlePolicySubmit() {
      try {
        // API 호출하여 policy_agree 업데이트
        await api.put(
          `/projects/${EstimateProject.id}/estimates/${EstimateProject.estimates[0].id}`,
          {
            policy_agree: true,
          }
        );

        // 모달 닫기
        setShowProjectAgreeModal(false);
        setPolicyAgree(false);
        setAbleToAgreeSubmit(false);
        sigCanvas.current.clear();
      } catch (error) {
        console.error('Error updating policy agree:', error);
        // 오류 발생 시 모달은 열린 상태로 유지
      }
    }

    return (
      <>
        <div className="text-end mb-3">
          <LanguageSwitcher />
        </div>
        <div className="bg-light p-4 mb-4">
          {i18n.language === 'ko' && (
            <>
              <Row className="mb-4">
                <Col xs={24}>
                  <div className="text-center">
                    <Title level={3}>HL만도 글로벌 협력사 온실가스 배출량 정보 등록</Title>
                    <Title level={4}>공급망 ESG 협력 이행 서약서</Title>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col xs={24}>
                  <p className="fs-6">
                    서약자는 HL만도의 온실가스 배출량 정보 등록 시스템을 통해 제공하는 모든 정보가
                    사실에 기반하고, 윤리적 기준을 준수함을 다음과 같이 서약합니다.
                  </p>
                </Col>
                <Col xs={24}>
                  <Divider />
                </Col>
                <Col xs={24}>
                  <p className="fs-6">
                    ① 서약자는 온실가스 배출량 정보 등록 시스템에 입력하는 사업장 정보, 온실가스
                    배출량, 총 매출액 등 모든 데이터 및 정보에 대해 사실에 기반하여 성실하고
                    정직하게 작성하겠습니다.
                  </p>
                  <p className="fs-6">
                    ② 서약자는 정보 제출 과정에서 고의적 누락, 허위작성, 증빙자료 조작 등 부정행위를
                    하지 않으며, 법적/윤리적 기준을 위반하지 않도록 최선을 다하겠습니다.
                  </p>
                  <p className="fs-6">
                    ③ 서약자는 제출한 정보에 대해 지속적인 책임을 지며, HL만도의 요청이 있을 경우
                    자료 보완을 성실히 수행하고 기타 필요한 협조를 제공하겠습니다.{' '}
                  </p>
                </Col>
              </Row>
            </>
          )}
          {i18n.language === 'en' && (
            <>
              <Row className="mb-4">
                <Col xs={24}>
                  <div className="text-center">
                    <Title level={4}>HL Mando Global Supplier GHG Emissions Data Submission</Title>
                    <Title level={5}>Supply Chain ESG Cooperation Commitment</Title>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col xs={24}>
                  <p className="fs-6">
                    The signatory hereby pledges that all information provided through HL Mando’s
                    GHG emissions data registration system is based on facts and complies with
                    ethical standards.
                  </p>
                </Col>
                <Col xs={24}>
                  <Divider />
                </Col>
                <Col xs={24}>
                  <p className="fs-6">
                    ① The signatory shall provide accurate and honest information regarding facility
                    details, GHG emissions, total revenue, and all other data and information
                    submitted to the GHG emissions data registration system.
                  </p>
                  <p className="fs-6">
                    ② The signatory shall not engage in misconduct such as intentional omissions,
                    false reporting, or manipulation of supporting documents during the submission
                    process, and shall make every effort to comply with legal and ethical standards.
                  </p>
                  <p className="fs-6">
                    ③ The signatory shall take ongoing responsibility for the submitted information,
                    provide supplementary data upon HL Mando’s request, and cooperate fully with any
                    additional requirements.{' '}
                  </p>
                </Col>
              </Row>
            </>
          )}
          {i18n.language === 'zh' && (
            <>
              <Row className="mb-4">
                <Col xs={24}>
                  <div className="text-center">
                    <Title level={4}>HL Mando全球合作伙伴温室气体排放信息登记</Title>
                    <Title level={5}>供应链ESG合作履约承诺书</Title>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col xs={24}>
                  <p className="fs-6">
                    承诺人郑重承诺，通过HL万都温室气体排放信息登记系统所提供的所有信息均基于事实，且遵守伦理标准。
                  </p>
                </Col>
                <Col xs={24}>
                  <Divider />
                </Col>
                <Col xs={24}>
                  <p className="fs-6">
                    ①
                    签署方应如实、准确地提供有关设施信息、温室气体排放、总收入以及提交至温室气体排放数据登记系统的所有其他数据和信息。
                  </p>
                  <p className="fs-6">
                    ②
                    承诺人在提交信息过程中，不进行故意遗漏、虚假填写、篡改证明材料等不当行为，并尽最大努力不违反法律和伦理标准。
                  </p>
                  <p className="fs-6">
                    ③
                    承诺人对所提交的信息承担持续责任，若HL万都提出要求，将积极配合补充资料并提供其他必要的协助。{' '}
                  </p>
                </Col>
              </Row>
            </>
          )}
          <Row>
            <Col xs={24}>
              <Divider />
            </Col>
            <Col xs={24} className="mb-3" style={{ fontSize: '14px' }}>
              <Row gutter={[12, 12]} className="mb-1">
                <Col xs={10} lg={i18n.language === 'en' ? 12 : 11}>
                  <div className="text-end">
                    {t('제출일')}:{'  '}
                  </div>
                </Col>
                <Col xs={12} lg={12}>
                  <div className="text-start">
                    {i18n.language === 'ko' && dayjs(agreeDate).format('YYYY년 MM월 DD일')}
                    {i18n.language === 'en' && dayjs(agreeDate).format('MM DD, YYYY')}
                    {i18n.language === 'zh' &&
                      dayjs(agreeDate).format('YYYY년 MM月 DD日 (提交日期)')}
                  </div>
                </Col>
              </Row>
              <Row gutter={[12, 12]} className="mb-1">
                <Col xs={10} lg={i18n.language === 'en' ? 12 : 11}>
                  <div className="text-end">
                    {t('기업명')}:{'  '}
                  </div>
                </Col>
                <Col xs={12} lg={12}>
                  <div className="text-start">{user.company_name}</div>
                </Col>
              </Row>
              <Row gutter={[12, 12]} className="mb-1">
                <Col xs={10} lg={i18n.language === 'en' ? 12 : 11}>
                  <div className="text-end">
                    {t('이메일')}:{'  '}
                  </div>
                </Col>
                <Col xs={12} lg={12}>
                  <div className="text-start">{user.email}</div>
                </Col>
              </Row>
              <Row gutter={[12, 12]}>
                <Col xs={10} lg={i18n.language === 'en' ? 12 : 11}>
                  <div className="text-end">
                    {t('서약자')}:{'  '}
                  </div>
                </Col>
                <Col xs={12} lg={12}>
                  <div className="text-start fs-6 mb-1">{user.username}</div>
                </Col>
              </Row>
            </Col>
            <Col xs={24} lg={{ span: 20, offset: 2 }} className="mb-3">
              <div className="d-flex align-items-center mx-auto">
                <div className="me-3" style={{ wordBreak: 'keep-all' }}>
                  {t('서명')}:
                </div>
                <div className="w-100 h-100 text-center">
                  <SignatureCanvas
                    ref={sigCanvas}
                    canvasProps={{
                      className: 'border border-gray-1',
                      style: {
                        backgroundColor: '#fff',
                        height: '100%',
                        width: '100%',
                        minHeight: '240px',
                        maxHeight: '320px',
                      },
                    }}
                    onEnd={handlePolicySubmitAvailable}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </div>
        <div className="text-center">
          <Button
            className="me-2"
            onClick={() => clearPolicySignature()}
            disabled={!isAbleToAgreeSubmit}
          >
            {t('서명 초기화')}
          </Button>
          <Button disabled={!isAbleToAgreeSubmit} onClick={() => handlePolicySubmit()}>
            {t('제출하기')}
          </Button>
        </div>
      </>
    );
  }

  const renderRemainingDays = () => {
    if (!EstimateProject || !EstimateProject['end_date']) {
      return '-';
    }

    const targetDate = dayjs(EstimateProject['end_date']);
    const now = dayjs();
    const diffInMs = targetDate.diff(now);

    // 음수면 만료
    if (diffInMs <= 0) {
      return i18n.language === 'ko' ? '만료됨' : i18n.language === 'en' ? 'Expired' : '已过期';
    }

    // 전체 일수와 나머지 시간 계산
    const totalDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    const remainingMs = diffInMs % (1000 * 60 * 60 * 24);
    const totalHours = Math.floor(remainingMs / (1000 * 60 * 60));

    return (
      <>
        {i18n.language === 'ko' && `${totalDays}일 ${totalHours} 시간`}
        {i18n.language === 'en' && `${totalDays} days ${totalHours} hours`}
        {i18n.language === 'zh' && `${totalDays}天 ${totalHours} 小时`}
      </>
    );
  };

  return (
    <BasicLayout body_classname="bg-light">
      {/* 디버깅 정보 - 임시 */}
      {import.meta.env.VITE_API_URL === 'development' && (
        <div
          style={{
            position: 'fixed',
            top: 30,
            right: 0,
            background: 'rgba(0,0,0,0.8)',
            color: 'white',
            padding: '10px',
            fontSize: '12px',
            zIndex: 9999,
            maxWidth: '300px',
          }}
        >
          <div>isLoadedState: {String(isLoadedState)}</div>
          <div>EstimateProject: {EstimateProject ? 'OK' : 'NULL'}</div>
          <div>EstimateProject type: {typeof EstimateProject}</div>
          <div>EstimateSites: {EstimateSites?.length || 0}</div>
          <div>project_code: {project_code}</div>
          <div>company_id: {company_id}</div>
          <div>RequiredDocs: {RequiredDocuments?.length || 0}</div>
          <div>OptionalDocs: {OptionalDocuments?.length || 0}</div>
          <div>Render condition: {String(isLoadedState && EstimateProject)}</div>
        </div>
      )}

      {/* 프로젝트 안내 모달 */}
      <ProjectAnnouncementModal
        isVisible={isShowProjectAnnouncementModal}
        onClose={() => showProjectAnnouncementModal(false)}
      />

      {/* 프로젝트 동의 사항 */}
      {isLoadedState && (
        <Modal
          title={t('프로젝트 동의')}
          open={isShowProjectAgreeModal}
          onOk={null}
          onCancel={null}
          width={'60%'}
          footer={null}
          style={{ maxWidth: '860px' }}
          className="my-4"
          centered
          closable={false}
          maskClosable={false}
        >
          <EstimateAgree />
        </Modal>
      )}

      {/* 참여자 관리 모달 */}
      <Modal
        title={t('참여자 관리')}
        open={isShowUsersModal}
        onOk={closeUserModal}
        onCancel={closeUserModal}
        width={'60%'}
        bodyStyle={{ padding: 0 }}
        footer={null}
        centered
      >
        <Layout>
          <Sider theme="light" width={'35%'} className="shadow-sm">
            <div className="d-flex justify-content-between align-items-center border-bottom p-3">
              <div>{t('등록된 사용자')}</div>
              <Button onClick={() => addNewUserHandler()}>{t('신규 사용자 초대')}</Button>
            </div>
            <div
              className="p-2"
              style={{ minHeight: '250px', height: 'calc(70vh)', overflowY: 'auto' }}
            >
              {siteUsers.map((user, user_idx) => (
                <div
                  className={clsx(
                    'px-3 py-2',
                    user && user.id == editUser.id && 'bg-primary text-light'
                  )}
                  key={`countries-${user_idx}-${user.email}`}
                  onClick={() => setEditUser(user)}
                >
                  {user_idx + 1}. {user.username}
                </div>
              ))}
            </div>
          </Sider>
          <Content>
            <div className="d-flex align-items-center justify-content-center h-100 p-3">
              {typeof editUser.id === 'undefined' && (
                <Paragraph>
                  {t('수정/삭제할 사용자를 선택하거나, 신규 사용자를 추가해 주세요')}
                </Paragraph>
              )}

              {typeof editUser.id !== 'undefined' && (
                <Card
                  type="inner"
                  title={editUser.id !== null ? t('사용자 정보 수정') : t('신규 사용자 초대하기')}
                  style={{ width: '80%' }}
                  actions={
                    editUser.id !== null
                      ? [
                          <Button type="link" disabled={!isDirty} key="save">
                            {t('저장')}
                          </Button>,
                          <Button
                            type="link"
                            disabled={!isDirty}
                            onClick={() => handleReset()}
                            key="reset"
                          >
                            {t('취소')}
                          </Button>,
                          <Button type="text" disabled={!isDirty} danger key="delete">
                            {t('삭제')}
                          </Button>,
                        ]
                      : [
                          <Button type="link" disabled={!isDirty} key="invite">
                            {t('초대하기')}
                          </Button>,
                        ]
                  }
                >
                  <Form
                    form={userEditForm}
                    labelCol={{ span: 6 }}
                    wrapperCol={{ span: 15 }}
                    initialValues={{ remember: true }}
                    autoComplete="off"
                    className="py-2"
                    onValuesChange={(_, allValues) => {
                      if (Object.values(allValues).some((_input) => isEmpty(_input))) {
                        setIsDirty(false);
                        return;
                      }

                      if (allValues['new-password'] !== allValues['new-password-confirm']) {
                        setIsDirty(false);
                        return;
                      }

                      setIsDirty(!isEqual(editUser, allValues));
                    }}
                  >
                    <Form.Item label={t('소속회사')} name="company_name" className="mb-3">
                      <Input disabled />
                    </Form.Item>

                    <Form.Item
                      label={t('성함')}
                      name="username"
                      rules={[{ required: true }]}
                      className="mb-3"
                    >
                      <Input />
                    </Form.Item>

                    <Form.Item
                      label={t('이메일')}
                      name="email"
                      rules={[{ required: true }]}
                      className={clsx(
                        editUser.id !== null && 'mb-3',
                        editUser.id === null && 'mb-0'
                      )}
                    >
                      <Input />
                    </Form.Item>

                    {editUser.id !== null && (
                      <>
                        <Form.Item
                          label={t('전화번호')}
                          name="phone"
                          rules={[{ required: true }]}
                          className="mb-3"
                        >
                          <Input />
                        </Form.Item>

                        <Form.Item
                          label={t('비밀번호 설정')}
                          name="current-password"
                          className="mb-3"
                        >
                          <Button onClick={() => setPasswordChange(!isPasswordChange)}>
                            {t('비밀번호 변경')}
                          </Button>
                        </Form.Item>

                        {!isPasswordChange && (
                          <Form.Item
                            label={t('비밀번호 초기화')}
                            name="current-password"
                            className="mb-3"
                          >
                            <Button disabled={isPasswordChange}>{t('비밀번호 초기화')}</Button>
                          </Form.Item>
                        )}
                      </>
                    )}

                    {editUser.id !== null && isPasswordChange && (
                      <>
                        <Form.Item
                          label={t('현재 비밀번호')}
                          name="current-password"
                          rules={[{ required: true }]}
                          className="mb-3"
                        >
                          <Input type="password" />
                        </Form.Item>

                        <Form.Item
                          label={t('변경할 비밀번호')}
                          name="new-password"
                          rules={[{ required: true }]}
                          className="mb-3"
                        >
                          <Input type="password" />
                        </Form.Item>
                        <Form.Item
                          label={t('변경할 비밀번호 확인')}
                          name="new-password-confirm"
                          rules={[{ required: true }]}
                          className="mb-0"
                        >
                          <Input type="password" />
                        </Form.Item>
                      </>
                    )}
                  </Form>
                </Card>
              )}
            </div>
          </Content>
        </Layout>
      </Modal>

      {/* 사업장 관리 모달 */}
      <SiteEditModal
        isVisible={isShowSiteEditModal}
        isEdit={isSiteEditModal}
        form={siteEditForm}
        onSave={saveSiteHandler}
        onCancel={closeSiteInfoEditModal}
        onClose={() => setSiteEditModal(false)}
        onReset={initSiteInfoEditModal}
      />

      {/* 품번 조회 */}
      <Modal
        title={`${t('품번조회')}`}
        open={isShowScope3TemplateModal}
        onCancel={() => setScope3TemplateModal(false)}
        width="60%"
        centered
        footer={[
          <div
            key="footer"
            style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <LanguageSwitcher />
            <Button onClick={() => setScope3TemplateModal(false)}>{t('닫기')}</Button>
          </div>,
        ]}
      >
        <MaterialSearchTable />
      </Modal>

      {/* 프로젝트 제목 */}
      {isLoadedState && EstimateProject && (
        <Row className="mb-4">
          <Col xs={24}>
            <Title level={1}>{EstimateProject[`project_name_${i18n.language}`]}</Title>
          </Col>
        </Row>
      )}

      {/* 디버깅: 프로젝트가 로드되지 않았을 때 */}
      {isLoadedState && !EstimateProject && (
        <Row className="mb-4">
          <Col xs={24}>
            <Title level={1} style={{ color: 'red' }}>
              DEBUG: EstimateProject is null/undefined
            </Title>
            <p>
              project_code: {project_code}, company_id: {company_id}
            </p>
          </Col>
        </Row>
      )}

      {/* 프로젝트 정보 */}
      {isLoadedState && EstimateProject && (
        <Row className="mb-5">
          <Col xs={24}>
            <Card className="shadow-sm">
              <Title level={3} className="text-dark mb-3">
                {t('프로젝트 정보')}
              </Title>
              <Row gutter={[24, 24]}>
                <Col xs={24} lg={12}>
                  <Descriptions
                    column={1}
                    size="small"
                    className="border"
                    labelStyle={STYLE_CONSTANTS.DESC_STYLE_HEADER}
                    contentStyle={STYLE_CONSTANTS.DESC_STYLE_BODY}
                    // layout="vertical"
                    bordered
                  >
                    <Descriptions.Item label={t('프로젝트명')}>
                      {EstimateProject[`project_name_${i18n.language}`]}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('기간')}>
                      {formatDateRange(
                        EstimateProject['start_date'],
                        EstimateProject['end_date'],
                        userTimezone
                      )}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('남은 기간')}>
                      {renderRemainingDays()}
                    </Descriptions.Item>
                    <Descriptions.Item label={t('산정 진행상태')}>
                      {EstimateProject?.estimates && EstimateProject?.estimates.length > 0 && (
                        <Tag
                          color={ProjectStatusCodeColor[EstimateProject?.estimates[0]['status']]}
                          style={{ fontSize: '12px', lineHeight: 2.1 }}
                        >
                          {t(ProjectStatusCode[EstimateProject?.estimates[0]['status']])}
                        </Tag>
                      )}
                      {!EstimateProject?.estimates ||
                        (EstimateProject?.estimates.length < 1 && ' ㅡ ')}
                    </Descriptions.Item>
                  </Descriptions>
                </Col>

                <Col xs={24} lg={12}>
                  <Descriptions
                    column={1}
                    size="small"
                    className="border"
                    labelStyle={STYLE_CONSTANTS.DESC_STYLE_HEADER}
                    contentStyle={STYLE_CONSTANTS.DESC_STYLE_BODY}
                    bordered
                  >
                    <Descriptions.Item label={t('산정 템플릿')}>
                      <div className="d-flex align-items-center">
                        <Select
                          value={templateType}
                          onChange={setTemplateType}
                          // style={{ width: 200, marginRight: 8 }}
                          options={[
                            {
                              value: 'latest',
                              label: `${t('온실가스 산정 템플릿')} v3`,
                            },
                            {
                              value: 'legacy',
                              label: t('Excel 2016 등 구버전 호환용'),
                            },
                          ]}
                        />
                        <Button icon={<DownloadOutlined />} onClick={downloadEmissionTemplate}>
                          {t('템플릿 다운로드')}
                        </Button>
                      </div>
                    </Descriptions.Item>
                    <Descriptions.Item label={t('안내사항 및 메뉴얼')}>
                      <Button className="me-2" onClick={() => showProjectAnnouncementModal(true)}>
                        {t('안내사항 보기')}
                      </Button>
                      <Button icon={<DownloadOutlined />} onClick={downloadManual}>
                        {t('메뉴얼 다운로드')}
                      </Button>
                    </Descriptions.Item>
                    {/* <Descriptions.Item label={t('참여자 관리')}>
                      <Button onClick={() => openUserModalHandler(company_id)}>
                        {t('참여자 관리')}
                      </Button>
                    </Descriptions.Item> */}
                  </Descriptions>
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      )}

      {/* 디버깅: 프로젝트 정보가 로드되지 않았을 때 */}
      {isLoadedState && !EstimateProject && (
        <Row className="mb-5">
          <Col xs={24}>
            <Card className="shadow-sm" style={{ borderColor: 'red' }}>
              <Title level={3} className="text-danger mb-3">
                DEBUG: 프로젝트 정보 로드 실패
              </Title>
              <p>
                API 호출 상태를 확인하세요. 브라우저 개발자 도구의 Console 탭에서 오류를 확인할 수
                있습니다.
              </p>
            </Card>
          </Col>
        </Row>
      )}

      {isLoadedState && (
        <Row className="mb-5">
          <Col xs={24}>
            <Card bodyStyle={{ paddingLeft: 0, paddingRight: 0, paddingBottom: 0 }}>
              <div className="d-flex justify-content-between align-items-bottom px-4">
                <Title level={3} className="text-dark mb-2">
                  {t('사업장')} {t('목록')}
                </Title>
                <Button onClick={() => openNewSiteModal()}>{t('사업장 추가')}</Button>
              </div>

              {!EstimateSites.length && (
                <Row>
                  <Col xs={24}>
                    <div className="text-center bg-gray-1 p-5 m-2">
                      <Title level={3} className="fw-medium text-gray-4 mb-0">
                        신규 사업장을 추가해주세요.
                      </Title>
                    </div>
                  </Col>
                </Row>
              )}
              {EstimateSites.length > 0 && (
                <Card
                  className="border-0"
                  onTabChange={(key) => setActiveEstimateSite(key)}
                  tabList={EstimateSites}
                  activeTabKey={ActiveEstimateSite}
                >
                  <Row className="mb-4">
                    <Col xs={24}>
                      <SiteDescriptionCard
                        ActiveEstimateSite={ActiveEstimateSite}
                        onEditSite={openSiteInfoEditModal}
                        descStyleHeader={STYLE_CONSTANTS.DESC_STYLE_HEADER}
                        descStyleBody={STYLE_CONSTANTS.DESC_STYLE_BODY}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={24} className="mb-3">
                      <Divider />
                    </Col>
                  </Row>
                  <Row>
                    <Col xs={24}>
                      <EstimateCycleTable
                        EstimateCycle={EstimateCycle || EstimateCylcle[company_id] || []}
                        onScope3TemplateModal={setShowScope3TemplateModal}
                        RequiredDocuments={RequiredDocuments}
                        OptionalDocuments={OptionalDocuments}
                        refreshCycleData={refreshCycleData}
                        onDownloadTemplate={downloadEmissionTemplate}
                      />
                    </Col>
                  </Row>
                </Card>
              )}
            </Card>
          </Col>
        </Row>
      )}
    </BasicLayout>
  );
}

export default UserSiteDetailed;
